import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './contexts/AuthContext';
import { AppInitializing } from './components/common';
import { MainLayout } from './components/layout/MainLayout';
import { RequireTenant, RequireRole } from './guards';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { NotFound } from './pages/NotFound';
import { ResourcesPage } from './pages/resources/ResourcesPage';
import { ResourceDetailPage } from './pages/resources/ResourceDetailPage';
import { CollectorsPage } from './pages/collectors/CollectorsPage';
import { DiscoveryPage } from './pages/discovery/DiscoveryPage';
import { AlertsPage } from './pages/alerts/AlertsPage';
import { AlertDetailPage } from './pages/alerts/AlertDetailPage';
import { AlertingPage } from './pages/alerting/AlertingPage';
import { DashboardsPage } from './pages/dashboards/DashboardsPage';
import { DashboardViewPage } from './pages/dashboards/DashboardViewPage';
import { StatusPagesPage } from './pages/status-pages/StatusPagesPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { PlatformPage } from './pages/platform/PlatformPage';
import { MigrationPage } from './pages/migration/MigrationPage';

/**
 * Route guards (frontend-patterns §8.3):
 *   RequireAuth   — signed in + tokenReady, else /login (return-to state)
 *   RequireTenant — a membership or a superadmin-entered tenant, else NoTenant
 *   RequireRole   — UI-side ladder gate (the API is the enforcement point)
 * RequireTenant and RequireRole live in ./guards (unit-tested without the
 * page tree); RequireAuth stays here because it renders the Login page.
 */

// Re-exported so the router's public surface is unchanged.
export { RequireRole };

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, tokenReady, bootstrapError } = useAuth();
  const location = useLocation();
  if (loading) return <AppInitializing />;
  if (!user || !tokenReady) {
    if (bootstrapError) {
      // Signed into Firebase but no usable Detect account — the login
      // page renders the bootstrap error with a way to sign out.
      return <Login />;
    }
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Platform panel: cross-tenant by definition — auth only, no
          tenant guard (a membership-less superadmin must reach it). */}
      <Route
        path="/platform"
        element={
          <RequireAuth>
            <PlatformPage />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <RequireTenant>
              <MainLayout />
            </RequireTenant>
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/:id" element={<ResourceDetailPage />} />
        <Route path="/collectors" element={<CollectorsPage />} />
        <Route path="/discovery" element={<DiscoveryPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/alerts/:id" element={<AlertDetailPage />} />
        <Route path="/alerting" element={<AlertingPage />} />
        <Route path="/dashboards" element={<DashboardsPage />} />
        <Route path="/dashboards/:id" element={<DashboardViewPage />} />
        <Route
          path="/status-pages"
          element={
            <RequireRole role="admin">
              <StatusPagesPage />
            </RequireRole>
          }
        />
        <Route
          path="/migration"
          element={
            <RequireRole role="admin">
              <MigrationPage />
            </RequireRole>
          }
        />
        {/* Settings is member-reachable: API tokens are self-scoped
            viewer-level; privileged tabs gate themselves. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
